import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { local } from "@/lib/storage";

export type ThemeChoice = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "esi.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
/** Each theme's `--surface-base` (tokens.css): the browser chrome's colour (`theme-color`). */
export const THEME_COLOR: Record<ResolvedTheme, string> = { light: "#FCFCFE", dark: "#0F1013" };

/** Sets the one `<meta name="theme-color">` (index.html has it; created if missing). */
function setThemeColor(theme: ResolvedTheme) {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.append(meta);
  }
  meta.content = THEME_COLOR[theme];
}

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function storedChoice(): ThemeChoice {
  const value = local.get(THEME_STORAGE_KEY);
  return value === "light" || value === "dark" ? value : "system";
}

const systemTheme = (): ResolvedTheme =>
  window.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";

/**
 * Keeps `<html data-theme>` in sync with the stored choice, following the OS live while the
 * choice is `system`. `public/theme-boot.js` applied the same rule before first paint.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(storedChoice);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) {
      return;
    }
    const onChange = () => setSystem(query.matches ? "dark" : "light");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolved = choice === "system" ? system : choice;
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    setThemeColor(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    local.set(THEME_STORAGE_KEY, next === "system" ? null : next);
  }, []);

  const value = useMemo(() => ({ choice, resolved, setChoice }), [choice, resolved, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme outside ThemeProvider");
  }
  return value;
}
